import { useCallback, useMemo, useState } from "react";

import * as Converter from "./converter";
import * as Handle from "./handle";

import type * as Type from "./type";
import type * as Repository from "../../_repository";

type Props = {
	hotelID: string;
	initSearchDate: string;
	optionPmsGetAvailableOptionListQueryData: Repository.Query.QueryData.OptionPmsGetAvailableOptionList.OptionPmsGetAvailableOptionListQueryData;
};

export const useViewModel = ({
	hotelID,
	initSearchDate,
	optionPmsGetAvailableOptionListQueryData: {
		optionPmsGetAvailableOptionList: { options },
	},
}: Props) => {
	const { onConvertViewModel } = Converter.useViewModelConverter();
	/**
	 * ViewModel の生成
	 */
	const initViewModel = useMemo(
		() => onConvertViewModel({ hotelID, initSearchDate }),
		[hotelID, initSearchDate, onConvertViewModel],
	);
	const [viewModel, setViewModel] = useState<Type.ViewModel>(initViewModel);

	const {
		onGetOption,
		onGetSubOptionList,
		onGetSubOption,
		onGetAvailableAmountOfOption,
		onGetAvailableAmountOfSubOption,
	} = Handle.useHandleOption({ options });
	/**
	 * オプションの選択可能な在庫が存在するか
	 */
	const onHasAvailableAmountOfOption = useCallback(
		(option?: Repository.Query.QueryData.OptionPmsGetAvailableOptionList.Option) => {
			const availableAmount = onGetAvailableAmountOfOption(option);
			return availableAmount === null || availableAmount === undefined ? false : availableAmount > 0;
		},
		[onGetAvailableAmountOfOption],
	);
	/**
	 * サブオプションの選択可能な在庫が存在するか
	 */
	const onHasAvailableAmountOfSubOption = useCallback(
		(subOption?: Repository.Query.QueryData.OptionPmsGetAvailableOptionList.SubOption) => {
			const availableAmount = onGetAvailableAmountOfSubOption(subOption);
			return availableAmount === null || availableAmount === undefined ? false : availableAmount > 0;
		},
		[onGetAvailableAmountOfSubOption],
	);

	/**
	 * ゲスト名の変更
	 */
	const onChangeGuestName = useCallback((value?: string) => {
		setViewModel((prev) => ({
			...prev,
			guestName: {
				value: value ?? "",
				errorMessage: !value
					? "お名前は必須項目です"
					: value.length > 100
						? "お名前は100文字以内で入力してください"
						: undefined,
			},
		}));
	}, []);

	/**
	 * 電話番号の変更
	 */
	const onChangeGuestTel = useCallback((guestTel?: string) => {
		setViewModel((prev) => ({
			...prev,
			guestTel: {
				value: guestTel ?? "",
				errorMessage: guestTel && guestTel.length > 15 ? "電話番号は15文字以内で入力してください" : undefined,
			},
		}));
	}, []);

	/**
	 * 日付の変更
	 */
	const onChangeDate = useCallback((date: string) => {
		setViewModel((prev) => ({
			...prev,
			date,
			optionID: {
				value: "",
				errorMessage: "オプションを選択してください",
			},
			subOptionID: undefined,
			amount: {
				value: undefined,
			},
		}));
	}, []);

	/**
	 * オプションの選択
	 */
	const onChangeOption = useCallback(
		(optionID: string) => {
			const option = onGetOption(optionID);
			const subOptionList = onGetSubOptionList(option);

			if (subOptionList) {
				setViewModel((prev) => ({
					...prev,
					optionID: {
						value: optionID,
					},
					subOptionID: {
						value: "",
						errorMessage: "サブオプションを選択してください",
					},
					amount: {
						value: undefined,
					},
				}));
			} else {
				/**
				 * 選択できる在庫が存在するか
				 */
				const hasAvailableAmount = onHasAvailableAmountOfOption(option);

				setViewModel((prev) => ({
					...prev,
					optionID: {
						value: optionID,
					},
					subOptionID: undefined,
					amount: {
						value: undefined,
						errorMessage: hasAvailableAmount
							? "選択可能な在庫が存在しませんでした"
							: "数量を選択してください",
					},
				}));
			}
		},
		[onGetOption, onGetSubOptionList, onHasAvailableAmountOfOption],
	);

	/**
	 * サブオプションの選択
	 */
	const onChangeSubOption = useCallback(
		(subOptionID: string) => {
			setViewModel((prev) => {
				const option = onGetOption(prev.optionID.value);
				const subOptionList = onGetSubOptionList(option);
				const subOption = onGetSubOption(subOptionList, subOptionID);
				const hasAvailableAmount = onHasAvailableAmountOfSubOption(subOption);

				return {
					...prev,
					subOptionID: {
						value: subOptionID,
					},
					amount: {
						value: undefined,
						errorMessage: hasAvailableAmount
							? "数量を選択してください"
							: "選択可能な在庫が存在しませんでした",
					},
				};
			});
		},
		[onGetOption, onGetSubOption, onGetSubOptionList, onHasAvailableAmountOfSubOption],
	);

	/**
	 * 数量の選択
	 */
	const onChangeAmount = useCallback((amount: number) => {
		setViewModel((prev) => ({
			...prev,
			amount: {
				value: amount,
			},
		}));
	}, []);

	/**
	 * コメントの変更
	 */
	const onChangeComment = useCallback((comment: string) => {
		setViewModel((prev) => ({
			...prev,
			comment: {
				value: comment,
				errorMessage: comment.length > 1000 ? "コメントは1000文字以内で入力してください" : undefined,
			},
		}));
	}, []);

	return {
		//
		viewModel,
		// ゲスト名の変更
		onChangeGuestName,
		// 電話番号の変更
		onChangeGuestTel,
		// 日付の変更
		onChangeDate,
		// オプションの変更
		onChangeOption,
		// サブオプションの変更
		onChangeSubOption,
		// 数量の変更
		onChangeAmount,
		// コメントの変更
		onChangeComment,
	};
};
